import { useEffect, useState } from "react";

import { get, isEmpty, isUndefined } from "@s-libs/micro-dash";
import { useRouter } from "next/router";

import menuOptions, {
  SIDEBAR_OPTIONS_ID,
  SIDEBAR_SECTIONS_ID,
} from "@/admin/AdminConsoleSidebar/AdminConsoleSidebarMenuData.constants";
import { USER_FEATURE_TO_CRUD_A_CONTENT_PROMOTION_CONFIGURATION } from "@/components/ContentPromotions/constants";
import getUserProperties from "@/users/getUserProperties";
import useHasFeature from "@/features/useHasFeature";
import useUser from "@/users/useUser";

/**
 * Method that add a submenu item into the collection.
 * The arguments are generated by the Angelou Anchor component, and are based on the menu Item definition.
 * @example
 * // returns [
 *  {
 *    id: 'directory',
 *    options: [{
 *      id: 'schools',
 *      title: 'Schools',
 *      relatedOptions: [{
 *        id: 'subItem',
 *        ariaLabel: 'subItem',
 *        title: 'subItem',
 *        url: '/url-test'
 *      }]
 *    }]
 * }]
 * addSubMenuItem(
 * [
 *  {
 *    id: 'directory',
 *    options: [{
 *      id: 'schools',
 *      title: 'Schools',
 *    }]
 * }],
 * 'directory',
 * 'test',
 *  {id: 'subItem', ariaLabel: 'subItem', title: 'subItem', url: '/url'}
 * );
 * @returns {Array} Returns a new copy of the menu options array with the new item.
 */
const addSubMenuItem = (
  menuOptions = [],
  menuSection,
  parentSection,
  subMenuItem = {},
) =>
  menuOptions.map((section) => {
    if (section.id === menuSection) {
      const { options } = section;
      return {
        ...section,
        options: options.map((option) => {
          return parentSection === option.id
            ? {
                ...option,
                relatedOptions: [subMenuItem],
              }
            : option;
        }),
      };
    }

    return section;
  });

const hasContentAdvisoryPrivileges = (features) =>
  get(features, ["content_advisory_upload"], "off") === "on";

/**
 * Hook that returns the sidebar menu options.
 * @param {Object} subMenuRecordInfo - subMenuInfo to add to the sidebar.
 *
 * @returns {Object} Returns an object with the sidebar data.
 */
const useAdminConsoleSidebarMenuOptions = ({ subMenuRecordInfo }) => {
  const { asPath } = useRouter();
  const { data: user } = useUser();

  const {
    isAuthorizedDistrictAdmin = false,
    isAuthorizedPrincipal = false,
    canViewContentSettings,
    canAccessImplementationGuide,
    hasTeacherRole,
  } = getUserProperties(user);

  const [canCrudAContentPromotionConfiguration] = useHasFeature(
    USER_FEATURE_TO_CRUD_A_CONTENT_PROMOTION_CONFIGURATION,
  );

  const filterMenuOptions = () => {
    // creates new menu to prevent altering existing menuOptions
    const newMenu = JSON.parse(JSON.stringify(menuOptions));

    return newMenu.map((section) => {
      if (section.id === "adminTools" || section.id === "settings") {
        const { options } = section;
        return {
          ...section,
          options: options.filter((option) => {
            const isContentAdvisoryTab = option.id === "content-advisory";
            const isContentSettingsTab = option.id === "content-settings";
            const isImplementationGuideTab = option.id === "admin-quick-start";
            const isContentPromotionTab = option.id === "content-promotion";

            if (
              isContentAdvisoryTab &&
              (!isAuthorizedDistrictAdmin ||
                (isAuthorizedDistrictAdmin &&
                  (!hasContentAdvisoryPrivileges(user.features) ||
                    canViewContentSettings)))
            )
              return false;

            if (isContentSettingsTab && !canViewContentSettings) return false;
            if (option.id === "reporting") return false;
            if (option.id === "settings" && !hasTeacherRole) return false;
            if (isImplementationGuideTab && !canAccessImplementationGuide)
              return false;
            if (isContentPromotionTab && !canCrudAContentPromotionConfiguration)
              return false;

            return true;
          }),
        };
      }
      return section;
    });
  };

  const checkIfNewMenuOption = (menuLinks, isLoadingNewMenuOption) => {
    const contentSettingsOptions = menuLinks
      .filter((section) => section.id === SIDEBAR_SECTIONS_ID.CONTENT_SETTINGS)
      .map((section) => section.options)
      .flat();

    const contentPromotionOption = contentSettingsOptions.filter(
      (option) => option.id === SIDEBAR_OPTIONS_ID.CONTENT_PROMOTION,
    )[0];

    if (
      !isUndefined(contentPromotionOption) &&
      isAuthorizedPrincipal &&
      !isAuthorizedDistrictAdmin &&
      !isLoadingNewMenuOption
    ) {
      contentPromotionOption.isNew = true;
    }
    return menuLinks;
  };

  const menuLinks = filterMenuOptions();
  const updatedMenuLinks = checkIfNewMenuOption(menuLinks);

  const [sideBarData, setSideBarData] = useState({
    menuItems: updatedMenuLinks,
    selectedId: "",
  });

  useEffect(() => {
    const section = asPath
      .split("/")
      .filter((pathSection) => !isEmpty(pathSection))[1];
    const selectedId = section ?? SIDEBAR_OPTIONS_ID.ADMIN_CONSOLE;
    if (!subMenuRecordInfo) {
      const menuLinks = filterMenuOptions();
      const updatedMenuLinks = checkIfNewMenuOption(menuLinks);
      setSideBarData({
        menuItems: updatedMenuLinks,
        selectedId,
      });
    }
  }, [subMenuRecordInfo]);

  useEffect(() => {
    const section = asPath
      .split("/")
      .filter((pathSection) => !isEmpty(pathSection))[1];
    let menuSection;
    switch (section) {
      case SIDEBAR_OPTIONS_ID.CLASS_ROOMS:
      case SIDEBAR_OPTIONS_ID.FACULTY:
      case SIDEBAR_OPTIONS_ID.SCHOOLS:
      case SIDEBAR_OPTIONS_ID.STUDENTS:
        menuSection = SIDEBAR_SECTIONS_ID.DIRECTORY;
        break;
      case SIDEBAR_OPTIONS_ID.REPORTING:
        menuSection = SIDEBAR_SECTIONS_ID.ADMIN_TOOLS;
        break;
      case SIDEBAR_OPTIONS_ID.CONTENT_ADVISORY:
      case SIDEBAR_OPTIONS_ID.CONTENT_RESTRICTION:
        menuSection = SIDEBAR_SECTIONS_ID.CONTENT_SETTINGS;
        break;
      default:
        menuSection = SIDEBAR_SECTIONS_ID.SETTINGS;
        break;
    }

    if (subMenuRecordInfo) {
      const reloadMenuLinks = checkIfNewMenuOption(filterMenuOptions());
      setSideBarData({
        selectedId: subMenuRecordInfo.id,
        menuItems: addSubMenuItem(reloadMenuLinks, menuSection, section, {
          ...subMenuRecordInfo,
          url: asPath,
        }),
      });
    } else {
      const selectedId = section ?? SIDEBAR_OPTIONS_ID.ADMIN_CONSOLE;
      const reloadMenuLinks = checkIfNewMenuOption(filterMenuOptions());
      setSideBarData({
        menuItems: reloadMenuLinks,
        selectedId,
      });
    }
  }, [subMenuRecordInfo]);
  return sideBarData;
};

export default useAdminConsoleSidebarMenuOptions;
